import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, CircularProgress } from '@mui/material';
import DownloadOutlined from '@mui/icons-material/DownloadOutlined';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TeamStatisticsHeader from './TeamStatisticsHeader';
import TeamStatisticsOverview from './TeamStatisticsOverview';
import { TEAM_PLAYER_GROUPS, teamPlayerMetric, teamPlayersQuery } from './TeamStatisticsPlayers';
import ReportGoalStatistics from '../reports/ReportGoalStatistics';
import ReportGlobalStatistics from '../reports/ReportGlobalStatistics';
import TablaResultadosVerticalEquipo from '../TablaResultadosVerticalEquipo';
import { playerName } from '../../utils/playerRanking';
import { createTeamStatisticsPdf } from '../../utils/teamStatisticsPdf';
import { isGoalkeeper } from '../../utils/playerDiscovery';
import PlayerRoleBadge from '../playerStatistics/PlayerRoleBadge';

export default function TeamStatisticsPdfExport({ team, statistics, standing, calendar, season, competition,
  group, disabled, incomplete }) {
  const { t, i18n } = useTranslation('stats');
  const client = useQueryClient();
  const navigate = useNavigate();
  const content = useRef();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [report, setReport] = useState(null);

  const prepare = async () => {
    if (busy) return;
    setBusy(true);
    setError(false);
    const snapshot = { team, statistics, standing, calendar, season, competition, group, incomplete };
    try {
      const players = await client.fetchQuery(teamPlayersQuery(team, navigate));
      setReport({ ...snapshot, players: [...players].sort((a, b) => playerName(a).localeCompare(playerName(b), i18n.language)) });
    } catch {
      setError(true);
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!report) return;
    let active = true;
    const timer = setTimeout(async () => {
      try {
        const pdf = createTeamStatisticsPdf(content.current, {
          teamName: report.team.nombre || t('teams.modern.team'), season: report.season,
          language: i18n.language, pageLabel: (page, total) => t('teams.modern.pdf.page', { page, total }),
        });
        const filename = `Soccertycs-${report.team.nombre || report.team.id}-${report.team.idTemporada}`
          .replace(/[^\p{L}\p{N} ._-]/gu, '');
        await pdf.save(`${filename}.pdf`, { returnPromise: true });
      } catch {
        if (active) setError(true);
      } finally {
        if (active) { setReport(null); setBusy(false); }
      }
    }, 0);
    return () => { active = false; clearTimeout(timer); };
  }, [report, t, i18n.language]);

  return <div className="ts-export-action">
    <Button variant="contained" disabled={disabled || busy} onClick={prepare} aria-busy={busy}
      startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <DownloadOutlined />}>
      {t(busy ? 'teams.modern.pdf.preparing' : 'teams.modern.export')}
    </Button>
    {error && <Alert severity="error">{t('teams.modern.pdf.error')}</Alert>}
    {report && <div className="rr-report ts-pdf-document" data-theme="light" aria-hidden="true" inert ref={content}>
      <p>{t('teams.modern.pdf.complete')}</p>
      {report.incomplete && <p>{t('teams.modern.partialData')}</p>}
      <TeamStatisticsHeader team={report.team} statistics={report.statistics} standing={report.standing}
        calendar={report.calendar} season={report.season} competition={report.competition} group={report.group} />
      <TeamStatisticsOverview team={report.team} statistics={report.statistics} standing={report.standing} calendar={report.calendar} />
      <h2>{t('teams.tabs.players')}</h2>
      <p>{t('teams.modern.playersDataNote')}</p>
      {!report.players.length && <p>{t('teams.modern.noPlayers')}</p>}
      {['participation', 'goals', 'discipline'].map(key => <section className="ts-pdf-players" key={key}>
        <h3>{t(`teams.modern.groups.${key}`)}</h3>
        <table aria-label={t(`teams.modern.groups.${key}`)}>
          <thead><tr><th>{t('teams.modern.player')}</th>
            {TEAM_PLAYER_GROUPS[key].map(metric => <th key={metric}>{t(`teams.modern.metrics.${metric}`)}</th>)}</tr></thead>
          <tbody>{report.players.map((player, index) => <tr key={player.id ?? index}
            className={isGoalkeeper(player) ? 'player-goalkeeper' : undefined}>
            <th scope="row">{playerName(player)}<PlayerRoleBadge player={player} /></th>{TEAM_PLAYER_GROUPS[key].map(metric =>
              <td key={metric}>{teamPlayerMetric(player, metric).toLocaleString(i18n.language, {
                maximumFractionDigits: metric === 'golesPorPartido' ? 2 : 1,
              })}</td>)}</tr>)}</tbody>
        </table>
      </section>)}
      <ReportGoalStatistics estadisticas={report.statistics} clasificacion={report.standing} missingValue="0" />
      <ReportGlobalStatistics estadisticas={report.statistics} clasificacion={report.standing} missingValue="0" />
      <h2>{t('teams.tabs.matches')}</h2>
      <TablaResultadosVerticalEquipo equipoSeleccionado={report.team.nombre} jornadas={report.calendar} />
    </div>}
  </div>;
}
